/*
 * Naturlabs PDP v2 behaviour (vanilla, no dependencies). Loaded with defer by the nl-w2-pdp-* sections; the guard
 * below makes it run once. Builds on nl-app.js (NL.cart, NL.formatMoney, NL.toast, <media-gallery>, <product-form>,
 * <sticky-atc>, <scroll-rail>) and nl-motion.js (NL.motion.reveal, reduced motion).
 *
 *  - Recently viewed: product handles in localStorage nl:recent:v1 (newest first, max 16, same key as nl-pdp.js),
 *    written only when Shopify's Customer Privacy API allows preference processing (or when it is not present).
 *  - <w2-pdp-nav>: anchor navigation with scroll spy (rAF, passive scroll, only while the details are near the
 *    viewport), sliding indicator, reading progress (desktop), smooth scroll that respects reduced motion.
 *  - <w2-pdp-rail data-source="complementary|related|recent">: lazy rails with THE product card.
 *  - <w2-bundle>: "Často kupováno společně", checkboxes, live total, one add to cart for all selected products.
 *  - <w2-delivery>: delivery estimate in Europe/Prague (weekends, Czech public holidays incl. Easter, closed dates).
 *  - <w2-ship>: free shipping progress kept in sync with cart:updated.
 *  - Gallery: pointer parallax of the packshot and its glow (fine pointers), thumbnail column follows the active
 *    slide, slide counter, videos pause when their slide is left, muted reels play only while visible and active,
 *    fullscreen zoom with pan (pointer follows) and previous / next.
 *  - <w2-stack>: "Proč je kvalitní" stacking cards (sticky from 1024 px, transform and opacity only).
 *  - Sticky bar: state sync for jumps past the main button, kept out of the way of the cookie consent banner.
 *  - Hlídat dostupnost: jump link from the sticky bar, success message focus after the contact form redirect.
 *  - Prices without trailing zeros in the bundle total and lines.
 */
(() => {
  'use strict';

  if (window.NLW2PDP) return;
  window.NLW2PDP = { version: '2.0.0' };

  const NL = window.NL || {};
  const RECENT_KEY = 'nl:recent:v1';
  const RECENT_MAX = 16;
  const CARD_TTL = 5 * 60 * 1000;
  const HANDLE_RE = /^[a-z0-9][a-z0-9_-]*$/i;

  const root = () => (NL.routes && NL.routes.root) || '/';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => (NL.motion && typeof NL.motion.reduced === 'boolean' ? NL.motion.reduced : motionQuery.matches);
  const designMode = () => document.documentElement.classList.contains('nl-design-mode');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  // Prices without trailing zeros ("1 047 Kč", "5,82 Kč"), like money_without_trailing_zeros in Liquid
  const money = (cents) => {
    const out =
      typeof NL.formatMoney === 'function' ? NL.formatMoney(cents) : `${(cents / 100).toFixed(2).replace('.', ',')} Kč`;
    return String(out).replace(/([.,])00(?=\D|$)/, '');
  };
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const reveal = (scope) => {
    if (NL.motion && typeof NL.motion.reveal === 'function') NL.motion.reveal(scope);
  };

  const getText = async (url) => {
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return { ok: false, status: response.status, text: '' };
      return { ok: true, status: response.status, text: await response.text() };
    } catch (error) {
      return { ok: false, status: 0, text: '' };
    }
  };

  const whenNear = (element, callback, margin = '800px 0px') => {
    const target = element.closest('.shopify-section') || element.parentElement || element;
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: margin }
    );
    observer.observe(target);
  };

  /* ------------------------------------------------------------------
   * Consent (Shopify Customer Privacy API): true when preference storage is allowed
   * ------------------------------------------------------------------ */
  const consent = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') {
          resolve(true);
          return;
        }
        resolve(Boolean(privacy.preferencesProcessingAllowed()));
      } catch (error) {
        resolve(true);
      }
    };
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) {
      finish();
    } else if (shopify && typeof shopify.loadFeatures === 'function') {
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => finish());
      } catch (error) {
        finish();
      }
      window.setTimeout(finish, 4000);
    } else {
      finish();
    }
  });

  /* ------------------------------------------------------------------
   * Recently viewed storage
   * ------------------------------------------------------------------ */
  const readRecent = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && HANDLE_RE.test(h)) : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecent = (list) => {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (error) {
      /* storage blocked or full */
    }
  };

  const trackView = async (handle) => {
    if (!handle || !HANDLE_RE.test(handle) || designMode()) return;
    if (!(await consent)) return;
    const list = readRecent().filter((h) => h !== handle);
    list.unshift(handle);
    writeRecent(list);
  };

  /* ------------------------------------------------------------------
   * <w2-pdp-nav>: anchor navigation with scroll spy
   * ------------------------------------------------------------------ */
  class W2Nav extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.links = Array.from(this.querySelectorAll('[data-w2-nav-link]'));
      this.targets = this.links.map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))));
      this.scroller = this.querySelector('[data-w2-nav-scroller]');
      this.indicator = this.querySelector('[data-w2-nav-indicator]');
      this.progress = this.querySelector('[data-w2-nav-progress]');
      this.details = this.closest('[data-w2-details]') || this.parentElement;
      this.active = -1;
      this.frame = 0;
      this.lockUntil = 0;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onResize = () => {
        this.place(true);
        this.onScroll();
      };

      this.links.forEach((link, index) => {
        link.addEventListener('click', (event) => {
          const target = this.targets[index];
          if (!target) return;
          event.preventDefault();
          this.setActive(index);
          this.lockUntil = Date.now() + (reduced() ? 50 : 900);
          target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
          try {
            window.history.replaceState(window.history.state, '', link.hash);
          } catch (error) {
            /* ignore */
          }
          window.setTimeout(() => target.focus({ preventScroll: true }), reduced() ? 0 : 650);
        });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            const near = entries.some((entry) => entry.isIntersecting);
            if (near && !this.listening) {
              this.listening = true;
              window.addEventListener('scroll', this.onScroll, { passive: true });
              this.onScroll();
            } else if (!near && this.listening) {
              this.listening = false;
              window.removeEventListener('scroll', this.onScroll);
              this.update();
            }
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this.details);
      } else {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      window.addEventListener('resize', this.onResize, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.place(true));
      this.update();
      this.place(true);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.listening = false;
      this._bound = false;
    }

    line() {
      const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nl-header-offset')) || 0;
      return offset + Math.min(window.innerHeight * 0.32, 280);
    }

    update() {
      if (!this.targets.length) return;
      const line = this.line();
      let index = 0;
      this.targets.forEach((target, i) => {
        if (target && target.getBoundingClientRect().top - line <= 0) index = i;
      });
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 4) index = this.targets.length - 1;
      if (Date.now() >= this.lockUntil) this.setActive(index);

      if (this.progress && this.details) {
        const rect = this.details.getBoundingClientRect();
        const value = clamp((line - rect.top) / Math.max(1, rect.height - window.innerHeight * 0.5), 0, 1);
        this.progress.style.transform = `scaleY(${value.toFixed(4)})`;
      }
    }

    setActive(index) {
      if (index === this.active || index < 0) return;
      this.active = index;
      this.links.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.place(false);
    }

    place(instant) {
      const link = this.links[this.active];
      if (!link || !this.indicator) return;
      const vertical = getComputedStyle(this).getPropertyValue('--w2-nav-vertical').trim() === '1';
      if (instant) this.indicator.style.transition = 'none';
      if (vertical) {
        this.indicator.style.width = '';
        this.indicator.style.height = `${link.offsetHeight}px`;
        this.indicator.style.transform = `translate3d(0, ${link.offsetTop}px, 0)`;
      } else {
        this.indicator.style.height = '';
        this.indicator.style.width = `${link.offsetWidth}px`;
        this.indicator.style.transform = `translate3d(${link.offsetLeft}px, 0, 0)`;
        if (this.scroller && this.scroller.scrollWidth > this.scroller.clientWidth + 2) {
          const left = link.offsetLeft - (this.scroller.clientWidth - link.offsetWidth) / 2;
          this.scroller.scrollTo({ left: Math.max(0, left), behavior: instant || reduced() ? 'auto' : 'smooth' });
        }
      }
      this.indicator.classList.add('is-ready');
      if (instant) {
        // force the instant position, then restore the transition for later moves
        void this.indicator.offsetWidth;
        this.indicator.style.transition = '';
      }
    }
  }

  define('w2-pdp-nav', W2Nav);

  /* ------------------------------------------------------------------
   * <w2-pdp-rail>
   * ------------------------------------------------------------------ */
  const readCardCache = (handle) => {
    try {
      const raw = window.sessionStorage.getItem(`nl:card:${handle}`);
      if (!raw) return '';
      const entry = JSON.parse(raw);
      return entry && Date.now() - entry.t < CARD_TTL ? entry.html : '';
    } catch (error) {
      return '';
    }
  };

  const writeCardCache = (handle, html) => {
    try {
      window.sessionStorage.setItem(`nl:card:${handle}`, JSON.stringify({ t: Date.now(), html }));
    } catch (error) {
      /* ignore */
    }
  };

  const fetchCard = async (handle) => {
    let html = readCardCache(handle);
    if (!html) {
      const result = await getText(`${root()}products/${encodeURIComponent(handle)}?section_id=nl-pdp-card`);
      if (!result.ok) {
        if (result.status === 404) writeRecent(readRecent().filter((h) => h !== handle));
        return null;
      }
      const card = parse(result.text).querySelector('.nl-card');
      if (!card) return null;
      html = card.outerHTML;
      writeCardCache(handle, html);
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.firstElementChild;
  };

  class W2Rail extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-w2-rail-track]');
      if (!this.track) return;
      if (this.dataset.source === 'recent') {
        this._onRecentWheel = (event) => this.onRecentWheel(event);
        this.track.addEventListener('wheel', this._onRecentWheel, { passive: false });
      }
      if (this.track.children.length) return;
      if (this.dataset.source !== 'recent' && !this.dataset.url) return;
      whenNear(this, () => this.load());
    }

    disconnectedCallback() {
      if (this.track && this._onRecentWheel) this.track.removeEventListener('wheel', this._onRecentWheel);
      this._onRecentWheel = null;
      this._bound = false;
    }

    onRecentWheel(event) {
      // Tiny horizontal trackpad drift must not trap a deliberate vertical page gesture.
      if (event.defaultPrevented || !event.cancelable || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (!Number.isFinite(event.deltaX) || !Number.isFinite(event.deltaY) || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      for (const element of event.composedPath()) {
        if (element === this.track) break;
        if (!(element instanceof Element)) continue;
        if (element.matches('input, select, textarea, [role="slider"], [role="spinbutton"], [role="listbox"], [role="combobox"], [role="scrollbar"]') || element.isContentEditable) return;
        const style = window.getComputedStyle(element);
        if (/^(auto|scroll)$/.test(style.overflowY) && element.scrollHeight > element.clientHeight) return;
      }
      let dy = event.deltaY;
      if (event.deltaMode === 1) {
        const style = window.getComputedStyle(this.track);
        dy *= parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 16) * 1.2;
      } else if (event.deltaMode === 2) {
        dy *= document.documentElement.clientHeight || window.innerHeight;
      } else if (event.deltaMode !== 0) {
        return;
      }
      const page = document.scrollingElement;
      if (!page || (dy < 0 && page.scrollTop <= 0) || (dy > 0 && page.scrollTop + page.clientHeight >= page.scrollHeight - 1)) return;
      event.preventDefault();
      window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
    }

    async items() {
      if (this.dataset.source === 'recent') {
        if (!(await consent)) return [];
        const limit = Math.max(1, parseInt(this.dataset.limit || '8', 10));
        const handles = readRecent()
          .filter((handle) => handle !== this.dataset.current)
          .slice(0, limit);
        const cards = (await Promise.all(handles.map((handle) => fetchCard(handle)))).filter(Boolean);
        return cards.map((card) => {
          const item = document.createElement('li');
          item.className = 'nl-rail__item';
          item.append(card);
          return item;
        });
      }
      const result = await getText(this.dataset.url);
      if (!result.ok) return [];
      const list = parse(result.text).querySelector('[data-w2-rail-items]');
      return list ? Array.from(list.children).filter((li) => li.querySelector('.nl-card')) : [];
    }

    async load() {
      const items = await this.items();
      if (!items.length) return;
      this.track.replaceChildren(...items.map((item) => document.importNode(item, true)));
      const band = this.closest('[data-empty]');
      if (band) band.removeAttribute('data-empty');
      const rail = this.querySelector('scroll-rail');
      if (rail && typeof rail.update === 'function') window.requestAnimationFrame(() => rail.update());
      reveal(this);
    }
  }

  define('w2-pdp-rail', W2Rail);

  /* ------------------------------------------------------------------
   * <w2-bundle>: Často kupováno společně
   * ------------------------------------------------------------------ */
  class W2Bundle extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.list = this.querySelector('[data-w2-bundle-list]');
      this.totalEl = this.querySelector('[data-w2-bundle-total]');
      this.countEl = this.querySelector('[data-w2-bundle-count]');
      this.linesEl = this.querySelector('[data-w2-bundle-lines]');
      this.button = this.querySelector('[data-w2-bundle-add]');
      this.shown = null;

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-w2-bundle-check]')) this.update();
      });
      if (this.button) this.button.addEventListener('click', () => this.add());

      this.onVariant = (event) => {
        const { variant, productId } = event.detail || {};
        if (!variant || String(productId) !== String(this.dataset.productId)) return;
        const item = this.list && this.list.querySelector('.nl-w2-bundle__item--current');
        if (!item) return;
        item.dataset.variantId = variant.id;
        item.dataset.price = variant.price;
        const price = item.querySelector('.nl-w2-bundle__price > span:last-child');
        if (price) price.textContent = money(variant.price);
        const check = item.querySelector('[data-w2-bundle-check]');
        if (check) {
          check.disabled = !variant.available;
          if (!variant.available) check.checked = false;
        }
        this.update();
      };
      document.addEventListener('variant:change', this.onVariant);

      if (this.dataset.url) whenNear(this, () => this.load());
      else this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariant);
      this._bound = false;
    }

    async fetchItems(url) {
      if (!url) return [];
      const result = await getText(url);
      if (!result.ok) return [];
      const list = parse(result.text).querySelector('[data-w2-bundle-recs]');
      if (!list) return [];
      return Array.from(list.querySelectorAll('[data-w2-bundle-item]')).filter(
        (item) => String(item.dataset.productId) !== String(this.dataset.productId)
      );
    }

    async load() {
      let items = await this.fetchItems(this.dataset.url);
      if (!items.length) items = await this.fetchItems(this.dataset.fallbackUrl);
      if (!items.length || !this.list) return;
      items.forEach((item) => this.list.append(document.importNode(item, true)));
      const band = this.closest('[data-empty]');
      if (band) band.removeAttribute('data-empty');
      this.update();
      reveal(this);
    }

    selected() {
      return Array.from(this.querySelectorAll('[data-w2-bundle-item]')).filter((item) => {
        const check = item.querySelector('[data-w2-bundle-check]');
        return check && check.checked && !check.disabled;
      });
    }

    update() {
      const items = Array.from(this.querySelectorAll('[data-w2-bundle-item]'));
      const selected = this.selected();
      items.forEach((item) => item.classList.toggle('is-off', !selected.includes(item)));
      const total = selected.reduce((sum, item) => sum + (parseInt(item.dataset.price, 10) || 0), 0);
      this.animateTotal(total);
      if (this.countEl) {
        const n = Math.min(4, Math.max(1, selected.length));
        this.countEl.textContent = selected.length ? this.getAttribute(`data-count-${n}`) || '' : '';
      }
      if (this.linesEl) {
        const lines = selected.map((item) => {
          const li = document.createElement('li');
          li.className = 'nl-w2-bundle__line';
          const name = document.createElement('span');
          name.className = 'nl-w2-bundle__line-name';
          name.textContent = item.dataset.title || '';
          const price = document.createElement('span');
          price.className = 'nl-w2-bundle__line-price';
          price.textContent = money(parseInt(item.dataset.price, 10) || 0);
          li.append(name, price);
          return li;
        });
        this.linesEl.replaceChildren(...lines);
      }
      if (this.button) {
        this.button.disabled = selected.length === 0;
        this.button.setAttribute('aria-disabled', String(selected.length === 0));
      }
    }

    animateTotal(total) {
      if (!this.totalEl) return;
      const from = this.shown === null ? total : this.shown;
      this.shown = total;
      window.cancelAnimationFrame(this.tween);
      if (from === total || reduced()) {
        this.totalEl.textContent = money(total);
        return;
      }
      const start = performance.now();
      const duration = 420;
      const step = (now) => {
        const t = clamp((now - start) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        this.totalEl.textContent = money(Math.round(from + (total - from) * eased));
        if (t < 1) this.tween = window.requestAnimationFrame(step);
      };
      this.tween = window.requestAnimationFrame(step);
    }

    async add() {
      const selected = this.selected();
      if (!selected.length || !this.button || !NL.cart) return;
      const items = selected.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
      this.button.setAttribute('aria-busy', 'true');
      try {
        await NL.cart.add(items, { source: 'bundle', opener: this.button });
      } catch (error) {
        const message = (error && error.message) || (NL.strings && NL.strings.cartError) || '';
        if (typeof NL.toast === 'function' && message) NL.toast(message, { variant: 'error' });
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  define('w2-bundle', W2Bundle);

  /* ------------------------------------------------------------------
   * <w2-delivery>: delivery estimate in Europe/Prague
   * ------------------------------------------------------------------ */
  const pad = (n) => String(n).padStart(2, '0');

  const easter = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return Date.UTC(year, month - 1, day, 12);
  };

  const holidayCache = {};
  const holidays = (year) => {
    if (holidayCache[year]) return holidayCache[year];
    const set = new Set(['01.01', '01.05', '08.05', '05.07', '06.07', '28.09', '28.10', '17.11', '24.12', '25.12', '26.12']);
    const sunday = easter(year);
    [-2, 1].forEach((offset) => {
      const date = new Date(sunday + offset * 864e5);
      set.add(`${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}`);
    });
    holidayCache[year] = set;
    return set;
  };

  const pragueNow = () => {
    try {
      const format = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Prague',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
      });
      const parts = {};
      format.formatToParts(new Date()).forEach((part) => {
        parts[part.type] = part.value;
      });
      return { y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour % 24 };
    } catch (error) {
      const now = new Date();
      return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(), h: now.getHours() };
    }
  };

  class W2Delivery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.text = this.querySelector('[data-w2-delivery-text]');
      this.compute();
      this.timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') this.compute();
      }, 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this._bound = false;
    }

    compute() {
      if (!this.text) return;
      const cutoff = parseInt(this.dataset.cutoff || '12', 10);
      const days = Math.max(1, parseInt(this.dataset.days || '1', 10));
      const weekend = this.dataset.weekend === 'true';
      const names = (this.dataset.names || '').split(',').map((s) => s.trim());
      const closed = new Set(
        (this.dataset.closed || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
          .map((value) => {
            const [d, m, y] = value.split('.').map((part) => parseInt(part, 10));
            return d && m && y ? `${d}.${m}.${y}` : '';
          })
      );
      const now = pragueNow();
      const base = Date.UTC(now.y, now.m - 1, now.d, 12);
      const at = (i) => new Date(base + i * 864e5);
      const isHoliday = (date) => holidays(date.getUTCFullYear()).has(`${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}`);
      const isClosed = (date) => closed.has(`${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`);

      let ship = -1;
      for (let i = 0; i <= 20; i += 1) {
        const date = at(i);
        const wd = date.getUTCDay();
        let ok = weekend || (wd !== 0 && wd !== 6);
        if (isHoliday(date) || isClosed(date)) ok = false;
        if (i === 0 && now.h >= cutoff) ok = false;
        if (ok) {
          ship = i;
          break;
        }
      }
      if (ship < 0) return;

      let deliver = -1;
      let counted = 0;
      for (let i = ship + 1; i <= ship + 20; i += 1) {
        const date = at(i);
        const wd = date.getUTCDay();
        if (wd === 0 || wd === 6 || isHoliday(date)) continue;
        counted += 1;
        if (counted >= days) {
          deliver = i;
          break;
        }
      }
      if (deliver < 0) return;

      const label = (i) => {
        if (i === 1) return this.dataset.tomorrow || '';
        const date = at(i);
        return `${names[date.getUTCDay()] || ''} ${date.getUTCDate()}. ${date.getUTCMonth() + 1}.`.trim();
      };
      const text =
        ship === 0
          ? (this.dataset.tplToday || '').replace('[cas]', `${cutoff}:00`).replace('[doruceni]', label(deliver))
          : (this.dataset.tplLater || '').replace('[expedice]', label(ship)).replace('[doruceni]', label(deliver));
      const clean = text.replace(/\.\./g, '.');
      if (clean && this.text.textContent !== clean) this.text.textContent = clean;
    }
  }

  define('w2-delivery', W2Delivery);

  /* ------------------------------------------------------------------
   * <w2-ship>: free shipping progress
   * ------------------------------------------------------------------ */
  class W2Ship extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.threshold = parseInt(this.dataset.threshold || '0', 10);
      this.text = this.querySelector('[data-w2-ship-text]');
      this.bar = this.querySelector('[data-w2-ship-bar]');
      this.onCart = (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart && typeof cart.total_price === 'number') this.render(cart.total_price);
      };
      document.addEventListener('cart:updated', this.onCart);
      const refresh = () => {
        if (!NL.cart || typeof NL.cart.get !== 'function') return;
        Promise.resolve(NL.cart.get())
          .then((cart) => {
            if (cart && typeof cart.total_price === 'number') this.render(cart.total_price);
          })
          .catch(() => {});
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(refresh, { timeout: 3000 });
      else window.setTimeout(refresh, 1500);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCart);
      this._bound = false;
    }

    render(total) {
      if (!this.threshold) return;
      const remaining = this.threshold - total;
      const progress = clamp(Math.floor((total * 100) / this.threshold), 0, 100);
      if (this.text) {
        this.text.innerHTML =
          remaining > 0
            ? (this.dataset.tplRemaining || '').replace('%amount%', money(remaining))
            : this.dataset.tplReached || '';
      }
      if (this.bar) {
        this.bar.style.setProperty('--progress', progress);
        this.bar.setAttribute('aria-valuenow', String(progress));
      }
      this.hidden = remaining <= 0 && this.dataset.showReached === 'false';
    }
  }

  define('w2-ship', W2Ship);

  /* ------------------------------------------------------------------
   * Gallery polish
   * ------------------------------------------------------------------ */
  const bindParallax = (stage) => {
    if (stage.dataset.w2ParallaxBound) return;
    stage.dataset.w2ParallaxBound = '1';
    let frame = 0;
    let x = 0;
    let y = 0;
    const apply = () => {
      frame = 0;
      stage.style.setProperty('--pdp-px', x.toFixed(3));
      stage.style.setProperty('--pdp-py', y.toFixed(3));
    };
    stage.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !fine.matches || reduced() || designMode()) return;
      const rect = stage.getBoundingClientRect();
      x = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
      y = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
      if (!frame) frame = window.requestAnimationFrame(apply);
    });
    stage.addEventListener('pointerleave', () => {
      x = 0;
      y = 0;
      if (!frame) frame = window.requestAnimationFrame(apply);
    });
  };

  const updateThumbsState = (list) => {
    const scrollable = list.scrollHeight - list.clientHeight > 2;
    list.toggleAttribute('data-scrollable', scrollable);
    list.toggleAttribute('data-at-end', !scrollable || list.scrollTop >= list.scrollHeight - list.clientHeight - 2);
  };

  const bindThumbs = (list) => {
    if (list.dataset.w2ThumbsBound) return;
    list.dataset.w2ThumbsBound = '1';
    list.addEventListener('scroll', () => updateThumbsState(list), { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(() => updateThumbsState(list)).observe(list);
    updateThumbsState(list);
  };

  const followActiveThumb = (gallery) => {
    const list = gallery.querySelector('.nl-pdp-gallery__thumbs');
    if (!list || list.scrollHeight <= list.clientHeight) return;
    const active = list.querySelector('[aria-current="true"]');
    const item = active && active.closest('li');
    if (!item) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    const behavior = reduced() ? 'auto' : 'smooth';
    if (top < list.scrollTop) list.scrollTo({ top: Math.max(0, top - 8), behavior });
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({ top: bottom - list.clientHeight + 8, behavior });
  };

  /* Reels: muted loops that play only while visible (and active inside a gallery), never under reduced motion */
  const reels = new Set();
  let reelObserver = null;

  const setReelState = (reel) => {
    const video = reel.querySelector('video');
    const toggle = reel.querySelector('[data-w2-reel-toggle]');
    if (!video) return;
    const slide = reel.closest('[data-gallery-slide]');
    const active = !slide || slide.hasAttribute('data-active');
    const wanted = reel._visible && active && !reel._userPaused && (!reduced() || reel._userPlayed) && !designMode();
    if (wanted && video.paused) {
      video.muted = true;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    } else if (!wanted && !video.paused) {
      video.pause();
    }
    if (toggle) {
      const playing = wanted;
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause || '' : toggle.dataset.labelPlay || '');
      reel.classList.toggle('is-playing', playing);
    }
  };

  const bindReel = (reel) => {
    if (reel.dataset.w2ReelBound) return;
    reel.dataset.w2ReelBound = '1';
    reels.add(reel);
    const video = reel.querySelector('video');
    if (video) {
      video.muted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
    }
    const toggle = reel.querySelector('[data-w2-reel-toggle]');
    if (toggle) {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const playing = reel.classList.contains('is-playing');
        reel._userPaused = playing;
        reel._userPlayed = !playing;
        setReelState(reel);
      });
    }
    if (!('IntersectionObserver' in window)) {
      reel._visible = true;
      setReelState(reel);
      return;
    }
    if (!reelObserver) {
      reelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target._visible = entry.isIntersecting;
            setReelState(entry.target);
          });
        },
        { threshold: 0.35 }
      );
    }
    reelObserver.observe(reel);
  };

  const pauseInactiveVideos = (gallery) => {
    gallery.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
      if (slide.hasAttribute('data-active')) return;
      slide.querySelectorAll('[data-w2-video] video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    });
    gallery.querySelectorAll('[data-w2-reel]').forEach(setReelState);
  };

  const updateCounter = (gallery, index) => {
    const counter = gallery.querySelector('[data-w2-gallery-index]');
    if (counter) counter.textContent = String(index + 1);
  };

  /* Zoom: pan by pointer, previous / next across zoomable slides */
  const zoomTriggers = (gallery) => Array.from(gallery.querySelectorAll('[data-gallery-zoom]'));

  const resetZoom = (stage) => {
    stage.classList.remove('is-zoomed');
    stage.style.removeProperty('--zx');
    stage.style.removeProperty('--zy');
  };

  const showZoom = (gallery, index) => {
    const triggers = zoomTriggers(gallery);
    if (!triggers.length) return;
    const i = (index + triggers.length) % triggers.length;
    gallery._w2ZoomIndex = i;
    const host = gallery.querySelector('[data-gallery-zoom-dialog]');
    if (!host) return;
    const image = host.querySelector('[data-zoom-image]');
    const stage = host.querySelector('[data-w2-zoom-stage]');
    const trigger = triggers[i];
    if (image && trigger.dataset.zoomSrc && image.getAttribute('src') !== trigger.dataset.zoomSrc) {
      image.src = trigger.dataset.zoomSrc;
      image.alt = trigger.dataset.zoomAlt || '';
    }
    if (stage) resetZoom(stage);
    const current = host.querySelector('[data-w2-zoom-index]');
    const total = host.querySelector('[data-w2-zoom-total]');
    if (current) current.textContent = String(i + 1);
    if (total) total.textContent = String(triggers.length);
    host.toggleAttribute('data-single', triggers.length < 2);
    const slide = trigger.closest('[data-gallery-slide]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const slideIndex = slides.indexOf(slide);
    if (slideIndex >= 0 && typeof gallery.goTo === 'function') gallery.goTo(slideIndex);
  };

  const bindZoom = (gallery) => {
    if (gallery.dataset.w2ZoomBound) return;
    gallery.dataset.w2ZoomBound = '1';
    const host = gallery.querySelector('[data-gallery-zoom-dialog]');
    if (!host) return;
    const stage = host.querySelector('[data-w2-zoom-stage]');

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gallery-zoom]');
      if (!trigger || !gallery.contains(trigger)) return;
      const i = zoomTriggers(gallery).indexOf(trigger);
      window.requestAnimationFrame(() => showZoom(gallery, i));
    });

    host.addEventListener('click', (event) => {
      const step = event.target.closest('[data-w2-zoom-step]');
      if (!step) return;
      event.preventDefault();
      showZoom(gallery, (gallery._w2ZoomIndex || 0) + parseInt(step.dataset.w2ZoomStep, 10));
    });

    host.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        showZoom(gallery, (gallery._w2ZoomIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    if (!stage) return;
    let frame = 0;
    let px = 50;
    let py = 50;
    let downX = 0;
    let downY = 0;
    const apply = () => {
      frame = 0;
      stage.style.setProperty('--zx', `${px.toFixed(2)}%`);
      stage.style.setProperty('--zy', `${py.toFixed(2)}%`);
    };
    const point = (event) => {
      const image = stage.querySelector('[data-zoom-image]');
      const rect = (image || stage).getBoundingClientRect();
      px = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      py = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    stage.addEventListener('pointerdown', (event) => {
      downX = event.clientX;
      downY = event.clientY;
    });
    stage.addEventListener('pointermove', (event) => {
      if (!stage.classList.contains('is-zoomed')) return;
      if (event.pointerType === 'mouse' || event.buttons) point(event);
    });
    stage.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      if (Math.abs(event.clientX - downX) > 6 || Math.abs(event.clientY - downY) > 6) return;
      if (stage.classList.contains('is-zoomed')) {
        resetZoom(stage);
      } else {
        point(event);
        apply();
        stage.classList.add('is-zoomed');
      }
    });
    document.addEventListener('dialog:close', () => resetZoom(stage));
  };

  const bindGallery = (gallery) => {
    if (gallery.dataset.w2GalleryBound) return;
    gallery.dataset.w2GalleryBound = '1';
    gallery.addEventListener('gallery:change', (event) => {
      const index = (event.detail && event.detail.index) || 0;
      updateCounter(gallery, index);
      followActiveThumb(gallery);
      pauseInactiveVideos(gallery);
    });
    bindZoom(gallery);
  };

  /* ------------------------------------------------------------------
   * <w2-stack>: "Proč je kvalitní" stacking cards (from 1024 px, motion allowed)
   *  - each item sticks a little lower than the previous one (--stack-top); a card taller than the space below the
   *    header sticks only once its end is visible (negative offset), so no content is ever hidden;
   *  - the covered card settles back: --stack-p (0..1) drives scale and a light shade (transform and opacity only);
   *  - rAF throttled passive scroll listener, active only while the stack is near the viewport.
   * ------------------------------------------------------------------ */
  const rootPx = (name, fallback) => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  class W2Stack extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.items = Array.from(this.querySelectorAll('[data-w2-stack-item]'));
      this.cards = this.items.map((item) => item.querySelector('.nl-w2-card'));
      this.mq = window.matchMedia('(min-width: 1024px)');
      this.frame = 0;
      this.near = false;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onResize = () => {
        if (!this.hasAttribute('data-stacking')) return;
        this.measure();
        this.onScroll();
      };
      this.onChange = () => this.setup();
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onChange);
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', this.onChange);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.onResize());
        this.items.forEach((item) => this.ro.observe(item));
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.near = entries.some((entry) => entry.isIntersecting);
            if (this.near) this.onScroll();
            // Off screen: back to the resting state, the next scroll into view recomputes it
            else
              this.cards.forEach((card) => {
                if (!card) return;
                card.style.removeProperty('--stack-p');
                card.style.removeProperty('--stack-q');
              });
          },
          { rootMargin: '300px 0px' }
        );
        this.io.observe(this);
      } else {
        this.near = true;
      }
      window.addEventListener('resize', this.onResize, { passive: true });
      this.setup();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onChange);
      if (motionQuery.removeEventListener) motionQuery.removeEventListener('change', this.onChange);
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      this._bound = false;
    }

    enabled() {
      return this.mq.matches && !reduced() && !designMode() && this.items.length > 1;
    }

    setup() {
      const on = this.enabled();
      this.toggleAttribute('data-stacking', on);
      window.removeEventListener('scroll', this.onScroll);
      if (on) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.measure();
        this.update();
      } else {
        this.items.forEach((item) => item.style.removeProperty('--stack-top'));
        this.cards.forEach((card) => {
          if (!card) return;
          card.style.removeProperty('--stack-p');
          card.style.removeProperty('--stack-q');
        });
      }
    }

    measure() {
      const header = rootPx('--nl-header-height', 82);
      const base = 96;
      const step = 16;
      this.tops = this.items.map((item, i) => {
        const height = item.offsetHeight;
        const wanted = base + i * step;
        const fit = window.innerHeight - height - 24 - header;
        const offset = Math.round(Math.min(wanted, fit));
        item.style.setProperty('--stack-top', `calc(var(--nl-header-offset) + ${offset}px)`);
        return offset;
      });
    }

    update() {
      if (!this.hasAttribute('data-stacking') || !this.near) return;
      const offset = rootPx('--nl-header-offset', 0);
      for (let i = 0; i < this.items.length - 1; i += 1) {
        const card = this.cards[i];
        if (!card) continue;
        const height = this.items[i].offsetHeight;
        const stuckTop = offset + (this.tops ? this.tops[i] : 96);
        const nextTop = this.items[i + 1].getBoundingClientRect().top;
        const nextStuck = offset + (this.tops ? this.tops[i + 1] : 112);
        // p: how much of the card the next one covers (scale, shade); q: the next card settles (content fade)
        const p = clamp(1 - (nextTop - stuckTop) / Math.max(1, height - 16), 0, 1);
        const q = clamp(1 - (nextTop - nextStuck) / 220, 0, 1);
        const value = p.toFixed(3);
        const fade = q.toFixed(3);
        if (card.style.getPropertyValue('--stack-p') !== value) card.style.setProperty('--stack-p', value);
        if (card.style.getPropertyValue('--stack-q') !== fade) card.style.setProperty('--stack-q', fade);
      }
    }
  }

  define('w2-stack', W2Stack);

  /* ------------------------------------------------------------------
   * Cookie consent banner: while Shopify's privacy banner is open, html[data-w2-consent-open] keeps the mobile
   * sticky add to cart bar out of its way (nl-w2-pdp.css)
   * ------------------------------------------------------------------ */
  const CONSENT_SELECTOR = '#shopify-pc__banner, .shopify-pc__banner__dialog';
  const consentOpen = () => {
    const banner = document.querySelector(CONSENT_SELECTOR);
    if (!banner) return false;
    const style = getComputedStyle(banner);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = banner.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0 && rect.top < window.innerHeight;
  };
  const syncConsent = () => document.documentElement.toggleAttribute('data-w2-consent-open', consentOpen());
  let consentWatching = false;
  const watchConsent = () => {
    if (consentWatching || !document.querySelector('sticky-atc.nl-w2-bar')) return;
    consentWatching = true;
    let frame = 0;
    const queue = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        syncConsent();
      });
    };
    let observed = null;
    const observeBanner = () => {
      const banner = document.querySelector(CONSENT_SELECTOR);
      const host = banner && (banner.closest('#shopify-pc__banner') || banner);
      if (!host || host === observed || !('MutationObserver' in window)) return;
      observed = host;
      new MutationObserver(queue).observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'],
      });
    };
    if ('MutationObserver' in window) {
      new MutationObserver(() => {
        observeBanner();
        queue();
      }).observe(document.body, { childList: true });
    }
    // The banner script loads late: check a few times after load as well
    let checks = 0;
    const timer = window.setInterval(() => {
      observeBanner();
      queue();
      checks += 1;
      if (checks >= 12) window.clearInterval(timer);
    }, 1000);
    window.addEventListener('load', queue);
    document.addEventListener('visitorConsentCollected', queue);
    document.addEventListener('click', () => window.setTimeout(queue, 400), { passive: true });
    observeBanner();
    queue();
  };

  /* ------------------------------------------------------------------
   * Sticky add to cart: <sticky-atc> (nl-app.js) learns that the main button is above the viewport from an
   * IntersectionObserver, which never fires when the page jumps from "button below the fold" straight to "button
   * above the viewport" (navigation links, scroll restoration, a long fling on phones). A light rAF scroll check
   * keeps its state right.
   * ------------------------------------------------------------------ */
  const bindStickySync = (bar) => {
    if (bar.dataset.w2SyncBound) return;
    bar.dataset.w2SyncBound = '1';
    const target = bar.dataset.target ? document.querySelector(bar.dataset.target) : null;
    if (!target) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      if (typeof bar.update !== 'function' || !('past' in bar)) return;
      const rect = target.getBoundingClientRect();
      const past = rect.bottom < 0;
      if (past !== bar.past) {
        bar.past = past;
        bar.update();
      }
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = window.requestAnimationFrame(check);
      },
      { passive: true }
    );
    window.addEventListener('load', check);
  };

  /* ------------------------------------------------------------------
   * Hlídat dostupnost: the sticky bar link scrolls to the form and focuses the e-mail field; after the redirect
   * (?contact_posted=true) the success message is brought into view and focused
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-w2-notify-jump]');
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
    const field = target.querySelector('input[type="email"]');
    if (field) window.setTimeout(() => field.focus({ preventScroll: true }), reduced() ? 0 : 500);
  });

  /* ------------------------------------------------------------------
   * Sold out: the quantity stepper is hidden next to the disabled "Vyprodáno" button; variant changes keep it in
   * sync (the server renders the first state, see sections/nl-w2-pdp-main.liquid)
   * ------------------------------------------------------------------ */
  document.addEventListener('variant:change', (event) => {
    const { variant, sectionId } = event.detail || {};
    if (!variant || !sectionId) return;
    const root = document.getElementById(`Pdp-${sectionId}`);
    if (!root) return;
    root.querySelectorAll('.nl-w2-buy .nl-pdp-buy__row[data-w2-qty-row]').forEach((row) => {
      row.classList.toggle('nl-pdp-buy__row--no-qty', !variant.available);
    });
  });

  const showNotifyDone = () => {
    const done = document.querySelector('[data-w2-notify-done]');
    if (!done) return;
    done.scrollIntoView({ behavior: 'auto', block: 'center' });
    done.focus({ preventScroll: true });
  };

  /* ------------------------------------------------------------------
   * Scan
   * ------------------------------------------------------------------ */
  const scan = (scope = document) => {
    scope.querySelectorAll('[data-w2-parallax]').forEach(bindParallax);
    scope.querySelectorAll('.nl-w2-gallery .nl-pdp-gallery__thumbs').forEach(bindThumbs);
    scope.querySelectorAll('[data-w2-gallery]').forEach(bindGallery);
    scope.querySelectorAll('[data-w2-reel]').forEach(bindReel);
    scope.querySelectorAll('[data-w2-track]').forEach((el) => trackView(el.dataset.w2Track));
    scope.querySelectorAll('sticky-atc.nl-w2-bar').forEach(bindStickySync);
    watchConsent();
  };

  if (/[?&]contact_posted=true/.test(window.location.search)) {
    if (document.readyState === 'complete') showNotifyDone();
    else window.addEventListener('load', showNotifyDone, { once: true });
  }

  if (motionQuery.addEventListener) {
    motionQuery.addEventListener('change', () => reels.forEach((reel) => setReelState(reel)));
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelectorAll) scan(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();
